import { componentScope, h } from "harmaja"
import * as L from "lonna"
import { Board, Item, expandToGroups, newContainer, newNote, newText } from "../../../common/src/domain"
import { Coordinates as Point } from "../../../common/src/geometry"
import { emptySet } from "../../../common/src/sets"
import { Dispatch } from "../store/board-store"
import { CRDTStore } from "../store/crdt-store"
import { BoardCoordinateHelper } from "./board-coordinates"
import { BoardFocus, getSelectedItems, noFocus } from "./board-focus"
import { dispatchDeletion } from "./item-delete"
import { dispatchDuplication } from "./item-duplicate"
import { groupSelected, ungroupSelected } from "./item-group"
import { voteOn } from "./item-vote"
import { bringToFront, sendToBack } from "./item-z-order"
import { Note } from "../../../common/src/domain"

type Entry = { label: string; shortcut?: string; action: () => void; disabled?: boolean } | "---"
type MenuState = { x: number; y: number; at: Point; kind: "item" | "canvas" } | null

const IS_MAC = typeof navigator !== "undefined" && /Mac/.test(navigator.platform)
const MOD = IS_MAC ? "⌘" : "Ctrl+"

// Right-click menu: verbs + shortcuts (formatting lives in the floating selection bar).
export function RightClickMenu({
    board,
    focus,
    dispatch,
    boardElement,
    coordinateHelper,
    crdtStore,
    latestNote,
    onAdd,
    zoomToFit,
    exportPng,
    pasteFromMirror,
    hasMirror,
    selectAll,
}: {
    board: L.Property<Board>
    focus: L.Atom<BoardFocus>
    dispatch: Dispatch
    boardElement: L.Property<HTMLElement | null>
    coordinateHelper: BoardCoordinateHelper
    crdtStore: CRDTStore
    latestNote: L.Property<Note>
    onAdd: (item: Item, at?: Point) => void
    zoomToFit: (rects?: Item[]) => void
    exportPng: () => void
    pasteFromMirror: (at: Point) => void
    hasMirror: () => boolean
    selectAll: () => void
}) {
    const state = L.atom<MenuState>(null)

    boardElement.forEach((el) => {
        if (!el) return
        el.addEventListener("contextmenu", (e) => {
            if (e.shiftKey) return // native menu escape hatch
            e.preventDefault()
            const itemEl = (e.target as HTMLElement).closest?.("[data-itemid]") as HTMLElement | null
            const at = coordinateHelper.pageToBoardCoordinates({ x: e.pageX, y: e.pageY })
            if (itemEl) {
                const id = itemEl.getAttribute("data-itemid")!
                const f = focus.get()
                const selected = f.status === "selected" ? f.itemIds : new Set<string>()
                if (!selected.has(id)) {
                    focus.set({
                        status: "selected",
                        itemIds: expandToGroups(board.get(), new Set([id])),
                        connectionIds: emptySet(),
                    })
                }
                state.set({ x: e.clientX, y: e.clientY, at, kind: "item" })
            } else {
                state.set({ x: e.clientX, y: e.clientY, at, kind: "canvas" })
            }
        })
    })
    const close = () => state.set(null)
    L.fromEvent(window, "mousedown")
        .pipe(L.applyScope(componentScope()))
        .forEach((e: any) => {
            if (!(e.target as HTMLElement).closest?.(".right-click-menu")) close()
        })
    L.fromEvent(window, "keydown")
        .pipe(L.applyScope(componentScope()))
        .forEach((e: any) => {
            if (e.key === "Escape") close()
        })
    L.fromEvent(window, "wheel").pipe(L.applyScope(componentScope())).forEach(close)
    L.fromEvent(window, "blur").pipe(L.applyScope(componentScope())).forEach(close)

    const run = (fn: () => void) => () => {
        close()
        fn()
    }

    function entries(s: NonNullable<MenuState>): Entry[] {
        const b = board.get()
        const f = focus.get()
        const items = getSelectedItems(b)(f)
        if (s.kind === "item") {
            const grouped = items.some((i) => !!i.groupId)
            const locked = items.some((i) => !!i.locked)
            const votable = items.some((i) => i.type === "note" || i.type === "text")
            const single = items.length === 1 ? items[0] : null
            return [
                { label: "Cut", shortcut: `${MOD}X`, action: () => document.execCommand("cut") },
                { label: "Copy", shortcut: `${MOD}C`, action: () => document.execCommand("copy") },
                { label: "Paste", shortcut: `${MOD}V`, action: () => pasteFromMirror(s.at), disabled: !hasMirror() },
                {
                    label: "Duplicate",
                    shortcut: `${MOD}D`,
                    action: () => dispatchDuplication(focus, b, dispatch, crdtStore),
                },
                { label: "Delete", shortcut: "⌫", action: () => dispatchDeletion(b.id, f, dispatch) },
                "---",
                grouped
                    ? { label: "Ungroup", shortcut: `⇧${MOD}G`, action: () => ungroupSelected(b, f, dispatch) }
                    : {
                          label: "Group",
                          shortcut: `${MOD}G`,
                          action: () => groupSelected(b, f, dispatch),
                          disabled: items.length < 2,
                      },
                { label: "Bring to front", shortcut: "]", action: () => bringToFront(b, f, dispatch) },
                { label: "Send to back", shortcut: "[", action: () => sendToBack(b, f, dispatch) },
                {
                    label: locked ? "Unlock" : "Lock",
                    action: () =>
                        dispatch({
                            action: "item.update",
                            boardId: b.id,
                            items: items.map((i) => ({ id: i.id, locked: locked ? false : "locked" })),
                        }),
                },
                "---",
                ...(votable
                    ? [{ label: "Vote", shortcut: "V", action: () => voteOn(b, items, 1, dispatch) } as Entry]
                    : []),
                { label: "Zoom to selection", shortcut: "⇧2", action: () => zoomToFit(items) },
                { label: "Export as PNG", shortcut: `⇧${MOD}E`, action: exportPng },
                ...(single
                    ? [
                          {
                              label: "Copy link to item",
                              action: () => {
                                  const url = `${location.origin}${location.pathname}#item=${single.id}`
                                  navigator.clipboard?.writeText(url)
                              },
                          } as Entry,
                      ]
                    : []),
            ]
        }
        const placeAt = (item: Item) => onAdd(item, s.at)
        return [
            { label: "Paste here", shortcut: `${MOD}V`, action: () => pasteFromMirror(s.at), disabled: !hasMirror() },
            "---",
            { label: "New note", shortcut: "N", action: () => placeAt(similarNote(latestNote.get())) },
            { label: "New text", shortcut: "T", action: () => placeAt(newText(b.crdt)) },
            { label: "New area", shortcut: "A", action: () => placeAt(newContainer(b.crdt)) },
            "---",
            { label: "Select all", shortcut: `${MOD}A`, action: selectAll },
            { label: "Zoom to fit", shortcut: "⇧1", action: () => zoomToFit() },
            {
                label: "Export board as PNG",
                shortcut: `⇧${MOD}E`,
                action: () => {
                    focus.set(noFocus)
                    exportPng()
                },
            },
        ]
    }

    return L.view(state, (s) => {
        try {
            return render(s)
        } catch (e) {
            console.error("Right-click menu failed", e)
            return null
        }
    })

    function render(s: MenuState) {
        return s === null ? null : (
            <div className="right-click-menu" style={menuStyle(s)} onContextMenu={(e) => e.preventDefault()}>
                {entries(s).map((entry) =>
                    entry === "---" ? (
                        <div className="separator" />
                    ) : (
                        <div
                            className={entry.disabled ? "entry disabled" : "entry"}
                            onClick={entry.disabled ? undefined : run(entry.action)}
                        >
                            <span className="label">{entry.label}</span>
                            {entry.shortcut ? <span className="shortcut">{entry.shortcut}</span> : null}
                        </div>
                    ),
                )}
            </div>
        )
    }
}

function similarNote(latest: Note): Note {
    return newNote("", latest.color, 20, 20, latest.width, latest.height, latest.shape)
}

function menuStyle(s: NonNullable<MenuState>) {
    // keep inside the viewport
    const w = 240
    const h = 420
    const x = Math.min(s.x, window.innerWidth - w - 8)
    const y = Math.min(s.y, window.innerHeight - h - 8)
    return { left: `${Math.max(8, x)}px`, top: `${Math.max(8, y)}px` }
}
